import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../Styles/OrderFulfillmentChatWidget.css';

const OF_CHAT_API_URL = process.env.REACT_APP_ORDER_FULFILLMENT_CHAT_API || 'https://dev-agent.savictech.com/of/odfapi/ask';
const OF_WIDGET_STORAGE_KEY = 'of_widget_state_v1';

const WELCOME_MESSAGE = {
  id: 'welcome',
  sender: 'ai',
  text: 'Hi, I am your Order Fulfillment Assistant. Ask me about risk, pending orders, plants, or delays.',
  followups: [
    'Which orders need attention today?',
    'Show high risk orders for Plant 1000',
  ],
};

const QUICK_PROMPTS = [
  'Show delayed orders with highest risk',
  'Which plant has max overdue orders?',
  'List pending orders for next 7 days',
];

function buildSession(title = 'New chat') {
  return {
    id: buildSessionId(),
    title,
    updatedAt: Date.now(),
    messages: [WELCOME_MESSAGE],
  };
}

function makeSessionTitle(question) {
  const clean = (question || '').trim();
  if (!clean) return 'New chat';
  return clean.length > 30 ? `${clean.slice(0, 30)}...` : clean;
}

function buildSessionId() {
  return `of-widget-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function extractAnswer(data) {
  return (
    data?.answer
    || data?.response
    || data?.message
    || (typeof data === 'string' ? data : JSON.stringify(data))
  );
}

function extractTablePayload(data) {
  const structured = data?.structured_response;
  if (!structured?.data) {
    return null;
  }

  const rawColumns = Array.isArray(structured.data.columns) ? structured.data.columns : [];
  const rawRows = Array.isArray(structured.data.rows) ? structured.data.rows : [];

  const normalizedColumns = rawColumns.length > 0
    ? rawColumns.map((column) => (typeof column === 'string' ? column : column?.key || column?.name || String(column)))
    : (rawRows[0] && typeof rawRows[0] === 'object' && !Array.isArray(rawRows[0])
      ? Object.keys(rawRows[0])
      : []);

  return {
    columns: normalizedColumns,
    rows: rawRows,
    pagination: structured.data.pagination || null,
    summary: structured.summary || null,
  };
}

function getRowCells(row, columns) {
  if (Array.isArray(row)) {
    return row;
  }

  if (row && typeof row === 'object') {
    return columns.map((column) => row[column]);
  }

  return [row];
}

function getInitialState() {
  try {
    const raw = localStorage.getItem(OF_WIDGET_STORAGE_KEY);
    if (!raw) {
      const initialSession = buildSession('Chat 1');
      return {
        open: true,
        expanded: false,
        fullScreen: false,
        activeSessionId: initialSession.id,
        sessions: [initialSession],
      };
    }

    const parsed = JSON.parse(raw);
    const savedSessions = Array.isArray(parsed.sessions) && parsed.sessions.length > 0
      ? parsed.sessions
      : [buildSession('Chat 1')];

    const activeSessionId = savedSessions.some((session) => session.id === parsed.activeSessionId)
      ? parsed.activeSessionId
      : savedSessions[0].id;

    return {
      open: typeof parsed.open === 'boolean' ? parsed.open : true,
      expanded: typeof parsed.expanded === 'boolean' ? parsed.expanded : false,
      fullScreen: typeof parsed.fullScreen === 'boolean' ? parsed.fullScreen : false,
      activeSessionId,
      sessions: savedSessions,
    };
  } catch (_error) {
    const initialSession = buildSession('Chat 1');
    return {
      open: true,
      expanded: false,
      fullScreen: false,
      activeSessionId: initialSession.id,
      sessions: [initialSession],
    };
  }
}

function nowLabel() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const OrderFulfillmentChatWidget = () => {
  const initial = useMemo(() => getInitialState(), []);
  const [open, setOpen] = useState(initial.open);
  const [expanded, setExpanded] = useState(initial.expanded);
  const [fullScreen, setFullScreen] = useState(initial.fullScreen);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState(initial.sessions);
  const [activeSessionId, setActiveSessionId] = useState(initial.activeSessionId);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const messagesEndRef = useRef(null);

  const activeSession = useMemo(
    () => sessions.find((session) => session.id === activeSessionId) || sessions[0],
    [sessions, activeSessionId]
  );

  const messages = activeSession?.messages || [WELCOME_MESSAGE];

  useEffect(() => {
    localStorage.setItem(
      OF_WIDGET_STORAGE_KEY,
      JSON.stringify({
        open,
        expanded,
        fullScreen,
        activeSessionId,
        sessions,
      })
    );
  }, [open, expanded, fullScreen, activeSessionId, sessions]);

  useEffect(() => {
    if (!fullScreen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setFullScreen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [fullScreen]);

  useEffect(() => {
    if (fullScreen) {
      document.body.classList.add('of-widget-fullscreen-active');
    } else {
      document.body.classList.remove('of-widget-fullscreen-active');
    }

    return () => {
      document.body.classList.remove('of-widget-fullscreen-active');
    };
  }, [fullScreen]);

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [open, messages, loading]);

  const canSend = useMemo(() => input.trim().length > 0 && !loading, [input, loading]);

  const replaceActiveSessionMessages = (nextMessages, nextTitle) => {
    setSessions((prev) =>
      prev.map((session) => {
        if (session.id !== activeSessionId) return session;
        return {
          ...session,
          title: nextTitle || session.title,
          messages: nextMessages,
          updatedAt: Date.now(),
        };
      })
    );
  };

  const sendMessage = async (question) => {
    const clean = (question || '').trim();
    if (!clean || loading) {
      return;
    }

    const userMsg = {
      id: `u-${Date.now()}`,
      sender: 'user',
      text: clean,
      time: nowLabel(),
    };

    const nextTitle = activeSession?.messages?.length <= 1 ? makeSessionTitle(clean) : activeSession?.title;
    const nextUserMessages = [...messages, userMsg];
    replaceActiveSessionMessages(nextUserMessages, nextTitle);
    setInput('');
    setError('');
    setLoading(true);

    try {
      const res = await fetch(OF_CHAT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: clean, session_id: activeSessionId }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }

      const aiMsg = {
        id: `a-${Date.now()}`,
        sender: 'ai',
        text: extractAnswer(data),
        followups: data?.suggested_followups || [],
        table: extractTablePayload(data),
        time: nowLabel(),
      };

      replaceActiveSessionMessages([...nextUserMessages, aiMsg]);
    } catch (err) {
      setError(err.message || 'Failed to fetch chatbot response');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSend) {
      return;
    }
    sendMessage(input);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (canSend) {
        sendMessage(input);
      }
    }
  };

  const handleNewChat = () => {
    const nextSession = buildSession(`Chat ${sessions.length + 1}`);
    setSessions((prev) => [nextSession, ...prev]);
    setActiveSessionId(nextSession.id);
    setInput('');
    setError('');
    setLoading(false);
  };

  const handleClearChat = () => {
    replaceActiveSessionMessages([WELCOME_MESSAGE], 'New chat');
    setError('');
  };

  const handleStartRenameSession = (session) => {
    setEditingSessionId(session.id);
    setRenameValue(session.title || 'New chat');
  };

  const handleCancelRenameSession = () => {
    setEditingSessionId(null);
    setRenameValue('');
  };

  const handleSaveRenameSession = (sessionId) => {
    const nextTitle = (renameValue || '').trim();
    if (!nextTitle) return;

    setSessions((prev) => prev.map((session) => (
      session.id === sessionId
        ? { ...session, title: nextTitle, updatedAt: Date.now() }
        : session
    )));
    setEditingSessionId(null);
    setRenameValue('');
  };

  const handleDeleteSession = (sessionId) => {
    setSessions((prev) => {
      const remaining = prev.filter((session) => session.id !== sessionId);

      if (remaining.length === 0) {
        const fallback = buildSession('Chat 1');
        setActiveSessionId(fallback.id);
        return [fallback];
      }

      if (activeSessionId === sessionId) {
        setActiveSessionId(remaining[0].id);
      }

      return remaining;
    });

    if (editingSessionId === sessionId) {
      handleCancelRenameSession();
    }
  };

  const handleToggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  const handleToggleFullScreen = () => {
    setFullScreen((prev) => {
      const next = !prev;
      if (next) {
        setExpanded(true);
      }
      return next;
    });
  };

  const sortedSessions = useMemo(
    () => [...sessions].sort((a, b) => Number(b.updatedAt || 0) - Number(a.updatedAt || 0)),
    [sessions]
  );

  const lastResponseTableRows = useMemo(() => {
    const lastAi = [...messages].reverse().find((message) => message.sender === 'ai' && message.table);
    return lastAi?.table?.rows?.length || 0;
  }, [messages]);

  return (
    <div className={`of-widget-container ${open ? 'is-open' : ''} ${fullScreen ? 'fullscreen' : ''}`}>
      {!open && (
        <button
          type="button"
          className="of-widget-toggle"
          onClick={() => setOpen(true)}
          aria-label="Open Order Fulfillment chat assistant"
        >
          Ask OF
        </button>
      )}

      {open && (
        <section
          className={`of-widget-panel ${expanded ? 'expanded' : ''}`}
          aria-label="Order Fulfillment Chat Widget"
        >
          <header className="of-widget-header">
            <div className="of-widget-title">
              <h3>Order Fulfillment Assistant</h3>
              <p>{loading ? 'Analyzing...' : 'Live API answers'} {lastResponseTableRows > 0 ? `- ${lastResponseTableRows} rows` : ''}</p>
            </div>
            <div className="of-widget-actions">
              <button type="button" onClick={handleNewChat}>New</button>
              <button type="button" onClick={handleClearChat}>Clear</button>
              <button type="button" onClick={handleToggleExpanded}>{expanded ? 'Min' : 'Max'}</button>
              {fullScreen ? (
                <button type="button" onClick={() => setFullScreen(false)}>Exit</button>
              ) : (
                <button type="button" onClick={handleToggleFullScreen}>Full</button>
              )}
              <button type="button" onClick={() => { setOpen(false); setFullScreen(false); }}>Close</button>
            </div>
          </header>

          <div className="of-widget-body">
            {expanded && (
              <aside className="of-widget-sessions" aria-label="Chat sessions">
                <h4>Sessions</h4>
                <div className="of-widget-session-list">
                  {sortedSessions.map((session) => (
                    <div
                      key={session.id}
                      className={`of-widget-session-item ${session.id === activeSessionId ? 'active' : ''}`}
                    >
                      {editingSessionId === session.id ? (
                        <div className="of-widget-session-rename" onClick={(event) => event.stopPropagation()}>
                          <input
                            value={renameValue}
                            onChange={(event) => setRenameValue(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter') {
                                event.preventDefault();
                                handleSaveRenameSession(session.id);
                              }
                              if (event.key === 'Escape') {
                                event.preventDefault();
                                handleCancelRenameSession();
                              }
                            }}
                            autoFocus
                          />
                          <div className="of-widget-session-rename-actions">
                            <button type="button" onClick={() => handleSaveRenameSession(session.id)}>Save</button>
                            <button type="button" onClick={handleCancelRenameSession}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="of-widget-session-main"
                            onClick={() => {
                              setActiveSessionId(session.id);
                              setError('');
                            }}
                          >
                            <strong>{session.title || 'New chat'}</strong>
                            <span>{new Date(session.updatedAt || Date.now()).toLocaleString([], { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                          </button>
                          <div className="of-widget-session-item-actions">
                            <button
                              type="button"
                              onClick={() => handleStartRenameSession(session)}
                              aria-label="Rename session"
                              title="Rename"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteSession(session.id)}
                              aria-label="Delete session"
                              title="Delete"
                            >
                              Del
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </aside>
            )}

            <div className="of-widget-main">
              <div className="of-widget-messages">
                {messages.length <= 1 && (
                  <div className="of-widget-quick-prompts">
                    {QUICK_PROMPTS.map((prompt) => (
                      <button key={prompt} type="button" onClick={() => sendMessage(prompt)}>{prompt}</button>
                    ))}
                  </div>
                )}

                {messages.map((message) => (
                  <article key={message.id} className={`of-widget-message ${message.sender === 'user' ? 'user' : 'ai'}`}>
                    <div className="of-widget-bubble">
                      <p>{message.text}</p>
                      {message.table?.rows?.length > 0 && (
                        <div className="of-widget-table-wrap">
                          <table className="of-widget-table">
                            <thead>
                              <tr>
                                {message.table.columns.map((column) => (
                                  <th key={column}>{column}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {message.table.rows.slice(0, 8).map((row, idx) => (
                                <tr key={`row-${idx}`}>
                                  {getRowCells(row, message.table.columns).map((cell, cIdx) => (
                                    <td key={`cell-${idx}-${cIdx}`}>{String(cell ?? '-')}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {!!message.followups?.length && (
                        <div className="of-widget-followups">
                          {message.followups.slice(0, 3).map((item) => (
                            <button key={item} type="button" onClick={() => sendMessage(item)}>{item}</button>
                          ))}
                        </div>
                      )}
                      <small className="of-widget-time">{message.time || ''}</small>
                    </div>
                  </article>
                ))}

                {loading && (
                  <div className="of-widget-typing">
                    <span />
                    <span />
                    <span />
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {error && <p className="of-widget-error">{error}</p>}

              <form className="of-widget-input" onSubmit={handleSubmit}>
                <textarea
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={2}
                  placeholder="Ask about pending orders, risk or plant status..."
                />
                <button type="submit" disabled={!canSend}>{loading ? 'Sending...' : 'Send'}</button>
              </form>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default OrderFulfillmentChatWidget;
